import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ClientPaymentInfo - ERP Phase 3.5
 * 
 * Read-only payment info for client portal.
 * Shows: monthly fee, booking mode, last renewal, current month status, payment date.
 * 
 * Client portal remains in Spanish.
 */
const ClientPaymentInfo = ({ clientId }) => {
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [infoRes, invRes] = await Promise.all([
          axios.get(`${API}/accounting/client/${clientId}/current`),
          axios.get(`${API}/invoices/mine`)
        ]);
        setData(infoRes.data);
        setInvoices(invRes.data || []);
      } catch (error) {
        console.error('Error fetching payment info:', error);
      } finally {
        setLoading(false);
      }
    };
    if (clientId) fetchAll();
  }, [clientId]);

  const downloadInvoice = async (iv) => {
    try {
      const res = await axios.get(`${API}/invoices/${iv.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert('No se pudo descargar la factura');
    }
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return '-';
    const [year, monthNum] = monthStr.split('-');
    const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusDisplay = (status) => {
    const config = {
      pending: { label: 'Pendiente', color: 'text-yellow-300', bg: 'bg-yellow-500/20' },
      paid: { label: 'Pagado', color: 'text-green-300', bg: 'bg-green-500/20' },
      issue: { label: 'Incidencia', color: 'text-red-300', bg: 'bg-red-500/20' }
    };
    return config[status] || { label: status, color: 'text-gray-300', bg: 'bg-gray-500/20' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-blue-300 text-center py-4">
        No hay información de pago disponible
      </div>
    );
  }

  const statusDisplay = getStatusDisplay(data.month_status);

  return (
    <div className="space-y-4" data-testid="client-payment-info">
      {/* Current month status - prominent */}
      <div className={`${statusDisplay.bg} rounded-lg p-4 text-center`}>
        <div className="text-blue-200 text-sm mb-1">{formatMonth(data.current_month)}</div>
        <div className={`text-2xl font-bold ${statusDisplay.color}`}>
          {statusDisplay.label}
        </div>
        {data.payment_date && (
          <div className="text-blue-200 text-sm mt-1">
            Pagado el {formatDate(data.payment_date)}
          </div>
        )}
      </div>

      {/* Plan details */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-blue-900/30 p-3 rounded-lg">
          <div className="text-blue-300 text-xs mb-1">Cuota mensual</div>
          <div className="text-white font-bold text-lg">
            {data.monthly_fee ? `${data.monthly_fee} ${data.currency}` : '-'}
          </div>
        </div>
        <div className="bg-blue-900/30 p-3 rounded-lg">
          <div className="text-blue-300 text-xs mb-1">Modalidad</div>
          <div className="text-white font-medium">
            {data.booking_mode === 'monthly_fixed' ? 'Mensual Fijo' :
             data.booking_mode === 'booking_flex' ? 'Bono Sesiones' : '-'}
          </div>
        </div>
      </div>

      {/* Last Renewal Date */}
      {data.last_renewal_date && (
        <div className="bg-blue-900/30 p-3 rounded-lg">
          <div className="text-blue-300 text-xs mb-1">Última renovación</div>
          <div className="text-white font-medium">
            {formatDate(data.last_renewal_date)}
          </div>
        </div>
      )}

      {/* Mis Facturas */}
      {invoices.length > 0 && (
        <div className="bg-blue-900/30 p-3 rounded-lg" data-testid="client-invoices">
          <div className="text-blue-300 text-xs mb-2">Facturas</div>
          <div className="space-y-2">
            {invoices.map((iv) => (
              <div key={iv.id} className="flex items-center justify-between text-sm">
                <div>
                  <span className="text-white font-medium">Factura {iv.invoice_number}</span>
                  <span className="text-blue-300 text-xs ml-2">{formatMonth(iv.payment_month)}</span>
                </div>
                <button
                  onClick={() => downloadInvoice(iv)}
                  className="text-blue-300 hover:text-white text-xs underline"
                  data-testid={`client-invoice-download-${iv.id}`}
                >
                  Descargar PDF
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-blue-400 text-xs text-center">
        Para consultas sobre pagos, contacta con tu entrenador.
      </p>
    </div>
  );
};

export default ClientPaymentInfo;
